<template>
    <filter-popover :value="value"
                    label-position="left"
                    :fields="fields"
                    :count="count"
                    :disabled="disabled"
                    with-organization
                    @change="change"
                    @clear="clear"
                    @confirm="confirm" />
</template>

<script>
import { resourceable } from '@/store/connectors'
import { types, kinds, memberSubRoles, translateKind } from '@/models/tasks'
import { filterToFilterForServer } from '@/models/task-filters'

import FilterPopover from '@/components/popovers/FilterPopover'

export default {
  components: {
    FilterPopover
  },
  mixins: [
    resourceable({
      on: 'dirsRevision', name: 'taskStatuses',
      watch: {
        from: 'type',
        to: type => ({
          filter: {
            task_type: type
          }
        }),
        immediate: true
      }
    })
  ],
  model: {
    prop: 'value',
    event: 'change'
  },
  props: {
    value: { type: Object, default: null },
    type: { type: String, default: null },
    disabled: { type: Boolean, default: false },
    deprecated: { type: Boolean, default: false }
  },
  computed: {
    taskStatusOptions() {
      return (this.taskStatuses || []).map(({ value, translated_name }) => ({
        value,
        label: translated_name
      }))
    },

    isDefectsType() { return this.type === types.DEFECTS_AND_VIOLATIONS },
    isBuildingOrderType() { return this.type === types.BUILDING_ORDER },
    isPhotoOrderType() { return this.type === types.PHOTO_ORDER },
    isAcceptanceType() { return this.type === types.ACCEPTANCE_OF_WORK },
    isApprovalType() { return this.type === types.APPROVAL_PROCESS },

    fields() {
      return [
        {
          type: 'input',
          propName: 'name',
          placeholder: 'Название',
          label: 'Название',
          group: 'Основное'
        },
        {
          type: 'input',
          propName: 'number_in_project',
          label: 'Номер в проекте',
          placeholder: 'Номер в проекте',
          group: 'Основное'
        },
        {
          type: 'multiselect',
          propName: 'status',
          label: 'Статус',
          options: this.taskStatusOptions,
          group: 'Основное'
        },
        ...this.isDefectsType && [{
          type: 'select',
          propName: 'kind_defect',
          label: 'Тип',
          options: Object.values(kinds).map(value => ({
            value,
            label: translateKind(value)
          })),
          group: 'Основное'
        }] || [],
        ...(this.isDefectsType || this.isAcceptanceType) && [{
          type: 'place',
          propName: 'place',
          group: 'Проект'
        }] || [],
        {
          type: 'user',
          placeholder: 'Инициатор',
          label: 'Инициатор',
          group: 'Пользователи',
          props: {
            isTaskCreator: true,
            withOrganizations: true,
            withDisabledSelection: true
          },
          from: value => value['creator'] || value['creatorOrganization'],
          to: value => ({
            'creator': value?._isUser && value,
            'creatorOrganization': value?._isOrganization && value
          })
        },
        ...this.isAcceptanceType && [{
          type: 'user',
          propName: 'acceptanceOfWorkContractor.id',
          placeholder: 'Подрядчик',
          label: 'Подрядчик',
          group: 'Пользователи',
          props: {
            isContractor: true,
            withOrganizations: true,
            withDisabledSelection: true
          },
          from: value => value['contractor'] || value['contractorOrganization'],
          to: value => ({
            'contractor': value?._isUser && value,
            'contractorOrganization': value?._isOrganization && value
          })
        }] || [],
        ...(this.isDefectsType || this.isBuildingOrderType) && [{
          type: 'user',
          propName: 'memberWorkers.user.id',
          placeholder: 'Подрядчик',
          label: 'Подрядчик',
          group: 'Пользователи',
          props: {
            isContractor: true,
            withOrganizations: true,
            withDisabledSelection: true
          },
          from: value => value['worker'] || value['workerOrganization'],
          to: value => ({
            'worker': value?._isUser && value,
            'workerOrganization': value?._isOrganization && value
          })
        }] || [],
        ...this.isDefectsType && [{
          type: 'user',
          propName: 'defectGeneralContractor.id',
          placeholder: 'Контроль',
          label: 'Контроль',
          group: 'Пользователи',
          props: {
            isInspector: true,
            withOrganizations: true,
            withDisabledSelection: true
          },
          from: value => value['inspector'] || value['inspectorOrganization'],
          to: value => ({
            'inspector': value?._isUser && value,
            'inspectorOrganization': value?._isOrganization && value
          })
        }] || [],
        ...this.isAcceptanceType && [{
          type: 'user',
          propName: 'memberWorkers.user.id',
          placeholder: 'Инженер стройконтроля от генподрядчика',
          label: 'Инженер стройконтроля от генподрядчика',
          group: 'Пользователи',
          props: {
            isEngineer: true,
            withOrganizations: true,
            withDisabledSelection: true
          },
          from: x => (x['memberWorkers.sub_role'] === memberSubRoles.WORKER_ACCEPTANCE_TASK_CONTROL_ENGINEER_FIRST_STAGE || null) && (x['worker'] || x['workerOrganization']),
          to: value => ({
            'worker': value?._isUser && value,
            'workerOrganization': value?._isOrganization && value,
            'memberWorkers.sub_role': value && memberSubRoles.WORKER_ACCEPTANCE_TASK_CONTROL_ENGINEER_FIRST_STAGE
          })
        }] || [],
        ...this.isAcceptanceType && [{
          type: 'user',
          propName: 'memberWorkers.user.id',
          placeholder: 'Инженер стройконтроля от заказчика',
          label: 'Инженер стройконтроля от заказчика',
          group: 'Пользователи',
          props: {
            isGeneralEngineer: true,
            withOrganizations: true,
            withDisabledSelection: true
          },
          from: x => (x['memberWorkers.sub_role'] === memberSubRoles.WORKER_ACCEPTANCE_TASK_CONTROL_ENGINEER_SECOND_STAGE || null) && (x['worker'] || x['workerOrganization']),
          to: value => ({
            'worker': value?._isUser && value,
            'workerOrganization': value?._isOrganization && value,
            'memberWorkers.sub_role': value && memberSubRoles.WORKER_ACCEPTANCE_TASK_CONTROL_ENGINEER_SECOND_STAGE
          })
        }] || [],
        ...this.isPhotoOrderType && [{
          type: 'user',
          propName: 'inspector',
          placeholder: 'Ответственный',
          label: 'Ответственный',
          group: 'Пользователи',
          props: {
            withOrganizations: true,
            withDisabledSelection: true
          },
          from: value => value['inspector'] || value['inspectorOrganization'],
          to: value => ({
            'inspector': value?._isUser && value,
            'inspectorOrganization': value?._isOrganization && value
          })
        }] || [],
        //...this.isDefectsType && [{
        //  type: 'input',
        //  propName: 'buildingOrderName',
        //  placeholder: 'Предписание',
        //  label: 'Предписание',
        //  group: 'Проект'
        //}] || [],
        {
          type: 'date',
          propName: 'created_from',
          label: 'Создан с',
          group: 'Даты'
        },
        {
          type: 'date',
          propName: 'created_to',
          label: 'Создан до',
          group: 'Даты'
        },
        ...this.type && !this.isAcceptanceType && !this.isApprovalType && [{
          type: 'date',
          propName: 'expired_from',
          label: 'Истекает с',
          group: 'Даты'
        }] || [],
        ...this.type && !this.isAcceptanceType && !this.isApprovalType && [{
          type: 'date',
          propName: 'expired_to',
          label: 'Истекает до',
          group: 'Даты'
        }] || [],
        ...this.isAcceptanceType && [{
          type: 'date',
          propName: 'started_from',
          label: 'Дата приемки с',
          group: 'Даты'
        }] || [],
        ...this.isAcceptanceType && [{
          type: 'date',
          propName: 'started_to',
          label: 'Дата приемки до',
          group: 'Даты'
        }] || [],
        ...this.isAcceptanceType && [{
          type: 'input',
          propName: 'ast_contract_number',
          label: 'Договор',
          placeholder: 'Номер договора',
          group: 'Договор АСТ'
        }] || [],
        ...this.isAcceptanceType && [{
          type: 'tag',
          propName: 'ast_act_ks2_enabled',
          options: ['С учетом объемов', 'Без учета объемов'].map((x, i) => ({
            value: `${!(i % 2)}`,
            label: x
          })),
          group: 'АСТ',
          from: filter => ({ value: filter['ast_act_ks2_enabled'] }),
          to: value => ({ 'ast_act_ks2_enabled': value?.value })
        }] || [],
        ...this.isAcceptanceType && [{
          type: 'tag',
          propName: 'has_ast_act_ks2',
          options: ['Есть КС-2 в АСТ', 'Нет КС-2 в АСТ'].map((x, i) => ({
            value: `${!(i % 2)}`,
            label: x
          })),
          group: 'АСТ',
          from: filter => ({ value: filter['has_ast_act_ks2'] }),
          to: value => ({ 'has_ast_act_ks2': value?.value })
        }] || [],
        ...this.isApprovalType && [{
          type: 'date',
          propName: 'approved_from',
          label: 'Согласовано с',
          group: 'Даты'
        }] || [],
        ...this.isApprovalType && [{
          type: 'date',
          propName: 'approved_to',
          label: 'Согласовано до',
          group: 'Даты'
        }] || [],
        {
          type: 'switch',
          propName: 'with_trashed',
          label: 'Показать удаленные',
          group: 'Прочее'
        },
        ...this.isApprovalType && [{
          type: 'tag',
          options: ['Изм.', 'Не изм.'].map((x, i) => ({
            value: !(i % 2),
            label: x
          })),
          propName: 'is_izm'
        }] || []
      ]
    },

    count() {
      return Object.entries(this.value || {})
        .filter(([_, v]) => v)
        .filter(([_, v]) => Array.isArray(v) ? v.length : true)
        .filter(([k, _]) => ![
          'type',
          'memberWorkers.sub_role'
        ].includes(k))
        .length
    }
  },
  methods: {
    change(x) {
      this.$emit('change', x)
    },

    clear() {
      this.$emit('clear')
    },

    confirm() {
      this.$emit('confirm', [this.value, this.serialize()])
    },

    serialize() { return filterToFilterForServer(this.value, { type: this.type, deprecated: this.deprecated }) }
  }
}
</script>
